<template xmlns="http://www.w3.org/1999/html">
  <section class="login register">
    <div class="login-wrapper">
      <div class="fp-logo">
        <img src="@/assets/images/logo.png" alt="FamiliPay logo">
      </div>
      <div class="login-form-wrapper box">
        <p class="small-text uppercase-text">{{ $lang.messages.welcome }}</p>
        <h1 class="title uppercase-text">{{ $lang.messages.phoneNumberVerification }}</h1>
        <hr class="horizontal-line">
        <form
          class="login-form"
          name="phoneNumberVerificationForm"
          novalidate
          @submit.prevent
        >
          <input name="hidden" type="text" style="display:none;">
          
          <!-- LABEL -->
          <p class="input-filter-label">
            {{ $lang.messages.phoneNumberVerificationNote1 }} <strong>{{ this.phoneNumberProp }}</strong> {{ $lang.messages.phoneNumberVerificationNote2 }}
          </p>
          <br>
          
          <!-- CODE -->
          <p class="input-filter-label">{{ $lang.messages.code }}</p>
          <div class="input-wrapper input-field" :class="{'error-input' : errors.has('code')}">
            <input
                type="text"
                class="input"
                :placeholder="$lang.messages.enterCode"
                name="code"
                v-model="code"
                required
                v-validate
                @focus.self="clearInputErrorsChild"
                @blur="clearOthersChild"
            >
            <img src="@/assets/images/validation_ico.png" alt="" class="icon">
            <div
                v-show="errors.has('code')"
                class="error-tooltip"
                :class="{'hide-tooltip': bluredPropChild !== 'code'}"
            >
              <div class="text">
                {{ errors.first('code') }}
              </div>
            </div>
          </div>
  
          <button class="verify-phone-link" @click="resendSMS">
            {{ $lang.messages.resendSMS}}
          </button>

          <!-- SUBMIT -->
          <f-p-input-button
            :buttonText="$lang.messages.send"
            :loading="loading"
            @formSubmit="onPhoneNumberVerificationFormSubmitted">
          </f-p-input-button>
          
        </form>
        
        <hr class="horizontal-line">
  
        <f-p-input-footer
          :regLabel="$lang.messages.ifYouHaveAccount"
          :leadsTo="'Login'"
          :regDesc="$lang.messages.loginHere">
        </f-p-input-footer>
        
      </div>
      
      <f-p-copyright
        :copLabel="$lang.messages.copyright"
        :copDesc="$lang.messages.familipay">
      </f-p-copyright>
      
    </div>
    <div class="footer register">
      <img src="@/assets/images/registration_illustration.png" class="registration-illustration">
    </div>
  </section>
</template>

<script>
  import { mapActions } from "vuex";
  import { RESEND_SMS, VERIFY_PHONE_NUMBER, LOGIN } from "@/store/action.types";
  import FPInputFooter from "@/components/common/inputs/FPInputFooter";
  import FPCopyright from "@/components/common/inputs/FPCopyright";
  import FPInputButton from "@/components/common/inputs/FPInputButton";
  import STATUS_CODES from "@/common/statusCodes";

  export default {
    name: "PhoneNumberVerification",
    components: {
      'f-p-input-footer': FPInputFooter,
      'f-p-copyright': FPCopyright,
      'f-p-input-button': FPInputButton
    },
    props: {
      bluredProp: {
        type: String,
        required: true
      },
      emailProp: {
        type: String,
        required: true
      },
      passwordProp: {
        type: String,
        required: true
      },
      phoneNumberProp: {
        type: String,
        required: true
      },
      loading: {
        type: Boolean,
        required: true
      },
      capsOn: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        bluredPropChild: '',
        phoneNumber: this.$route.query.phoneNumber,
        code: ''
      }
    },
    methods: {
      ...mapActions({
        resendSMSAction: RESEND_SMS,
        verifyPhoneNumberAction: VERIFY_PHONE_NUMBER,
        loginAction: LOGIN
      }),
      resendSMS() {
        this.resendSMSAction({
          phoneNumber: this.phoneNumberProp
        })
        .then(() => {
          this.$snotify.success(this.$lang.messages.sn_smsResent, 'SUCCESS');
        })
        .catch(error => {
          this.$emit('isLoading', false);
          this.$snotify.error(error.data.status.desc, 'ERROR')
        });
      },
      onPhoneNumberVerificationFormSubmitted() {
        // On form submit remove the error toolip, but leave the error on input field
        this.$emit('clearInputs');
        this.checkAnyInput();
        
        this.$validator.validateAll().then(result => {
          this.setMarginTooltip();
          
          if (result) {
            // On form submit show the loading spinner
            this.$emit('isLoading', true);
            
            this.verifyPhoneNumberAction({
              smsCode : this.code,
              phoneNumber: this.phoneNumberProp
            })
            .then(() => {
              this.$snotify.success(this.$lang.messages.sn_phoneNumberVerified, 'SUCCESS');
              this.login();
            })
            .catch(error => {
              this.$emit('isLoading', false);
              this.$snotify.error(error.data.status.desc, 'ERROR')
            });
          }
        });
      },
      login() {
        this.loginAction({
          email: this.emailProp,
          password: this.passwordProp
        })
        .then(response => {
          if (response.data.data.countryCode === "US") {
            this.$lang.setLang("us")
          } else if (response.data.data.countryCode === "GB") {
            this.$lang.setLang("uk")
          }
    
          // On form submit after response remove the loading spinner
          this.$emit('isLoading', false);
          this.$router.push({ name: "Preferences" })
        })
        .catch(error => {
          // On form submit after any error in response remove the loading spinner
          this.$emit('isLoading', false);
    
          if (error.data.status.code === STATUS_CODES.FP_400 || error.data.status.code === STATUS_CODES.FP_401) {
            this.$snotify.error(this.$lang.messages.sn_invalidCredentials, "ERROR")
          } else if (error.data.status.code === STATUS_CODES.FP_403) {
            this.$snotify.error(this.$lang.messages.sn_loginEmailConfirm, "ERROR")
          }
          this.$router.push({ name: "Login" })
        });
      },
      setMarginTooltip() {
        let errorTooltip = document.getElementsByClassName('error-tooltip');
        
        for (let item of errorTooltip) {
          let height = item.offsetHeight;
          if (height >= 60) {
            item.style.marginTop = "-40%"
          } else {
            item.style.marginTop = "-85px"
          }
        }
      },
      clearInputErrorsChild(e) {
        // Remove bluredProp from LandingLayout
        this.$emit('clearBluredPropLandingLayout');
        
        if (e.target) {
          this.$validator.errors.remove(e.target.name)
        } else {
          this.$validator.errors.remove(e)
        }
      },
      checkAnyInput() {
        this.bluredPropChild = ''
      },
      clearOthersChild(e) {
        this.bluredPropChild = e.target.name
      },
      // Clear input errors from children, and send value to LandingLayout
      clearOthers (val) {
        this.$emit('clearOthers', val)
      }
    }
  };
</script>
